import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { User, Building2, Settings, CheckCircle, ArrowRight, ArrowLeft, Bot, Plus, X, Trash2, Edit2, Mail, Phone, MapPin, AlertCircle } from 'lucide-react';
import { createResource } from '../../lib/resources';
import { getResourceRoles, ResourceRole } from '../../lib/resourceRoles';


interface SetupStep {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  completed: boolean;
}

export default function Setup() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState<string | null>(null);
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null);
  const [selectedServiceType, setSelectedServiceType] = useState<string | null>(null);
  const [setupData, setSetupData] = useState<{}>({});

  const [selectedTeamOption, setSelectedTeamOption] = useState<string | null>(null);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [createdTeamMembers, setCreatedTeamMembers] = useState<any[]>([]);
  const [justMeFormData, setJustMeFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    compensation_type: 'hourly' as 'hourly' | 'flat_rate' | 'commission',
    hourly_rate: '0',
    flat_rate_amount: '0',
    commission_percentage: '0',
    notes: ''
  });
  const [justMeFormLoading, setJustMeFormLoading] = useState(false);
  const [justMeFormError, setJustMeFormError] = useState<string | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [showServiceAreaForm, setShowServiceAreaForm] = useState(false);
  const [serviceAreas, setServiceAreas] = useState<any[]>([]);
  const [newServiceArea, setNewServiceArea] = useState({
    name: '',
    postal_codes: ['']
  });
  const [editingServiceArea, setEditingServiceArea] = useState<any | null>(null);
  const [deletingServiceArea, setDeletingServiceArea] = useState<any | null>(null);
  const [serviceAreaLoading, setServiceAreaLoading] = useState(false);
  const [serviceAreaError, setServiceAreaError] = useState<string | null>(null);

  // Business Information state
  const [businessInfo, setBusinessInfo] = useState({
    business_name: '',
    business_email: '',
    business_phone: '',
    business_address: ''
  });
  const [businessInfoLoading, setBusinessInfoLoading] = useState(false);
  const [businessInfoError, setBusinessInfoError] = useState<string | null>(null);

  // Final step state
  const [finalStepSelection, setFinalStepSelection] = useState<string | null>(null);

  // Team member form state
  const [showTeamMemberForm, setShowTeamMemberForm] = useState(false);
  const [teamMemberLoading, setTeamMemberLoading] = useState(false);
  const [teamMemberError, setTeamMemberError] = useState<string | null>(null);
  const [permissionRoles, setPermissionRoles] = useState<ResourceRole[]>([]);
  const [teamMemberFormData, setTeamMemberFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: '',
    role_id: '',
    compensation_type: 'hourly' as 'hourly' | 'flat_rate' | 'commission',
    hourly_rate: '0',
    flat_rate_amount: '0',
    commission_percentage: '0',
    status: 'active',
    is_admin: false,
    password: '',
    confirm_password: '',
    notes: ''
  });

  // State for Apply Area to Team step
  const [mobileLoading, setMobileLoading] = useState<string | null>(null);

  // Dynamic setup steps based on service type
  const getSetupSteps = (): SetupStep[] => {
    // All flows now have the same simplified steps
      return [
        {
          id: 1,
          title: "Welcome",
          description: "Meet Nexus AI and get started",
          icon: <Bot className="h-6 w-6" />,
          completed: false
        },
        {
          id: 2,
          title: "Business Information",
          description: "Add your business details",
          icon: <Building2 className="h-6 w-6" />,
          completed: false
        },
        {
          id: 3,
          title: "Industry Selection",
          description: "Select your business industry",
          icon: <Building2 className="h-6 w-6" />,
          completed: false
        },
        {
          id: 4,
          title: "Service Type",
          description: "Select your service delivery method",
          icon: <Settings className="h-6 w-6" />,
          completed: false
        },
        {
          id: 5,
          title: "Completed Basic Setup",
          description: "Finish setup and enter the system",
          icon: <CheckCircle className="h-6 w-6" />,
          completed: false
        }
      ];
  };

  const setupSteps = getSetupSteps();

  useEffect(() => {
    const { userId: uid, email: userEmail } = location.state || {};
    if (!uid || !userEmail) {
      navigate('/login');
      return;
    }

    setUserId(uid);
    setEmail(userEmail);
    fetchBusinessName(uid);
    fetchBusinessId(uid);
  }, [location.state, navigate]);

  const fetchBusinessName = async (userId: string) => {
    if (!supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('businesses')
        .select('name')
        .eq('owner_id', userId)
        .single();

      if (error) {
        console.error('Error fetching business name:', error);
        return;
      }

      if (data) {
        setBusinessName(data.name);
      }
    } catch (err) {
      console.error('Error fetching business name:', err);
    }
  };

  const fetchBusinessId = async (userId: string) => {
    if (!supabase) return;
    
    try {
      console.log('Fetching business ID for userId:', userId);
      const { data, error } = await supabase
        .from('businesses')
        .select('id')
        .eq('owner_id', userId)
        .single();

      if (error) {
        console.error('Error fetching business ID:', error);
        return;
      }

      if (data) {
        console.log('Business ID fetched:', data.id);
        setBusinessId(data.id);
        // Load existing team members
        loadTeamMembers(data.id);
        // Load existing service areas
        loadServiceAreas(data.id);
        // Load business information
        loadBusinessInfo(userId);
      } else {
        console.error('No business found for userId:', userId);
      }
    } catch (err) {
      console.error('Error fetching business ID:', err);
    }
  };

  const loadBusinessInfo = async (userId: string) => {
    if (!supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('businesses')
        .select('name, email, phone, address')
        .eq('owner_id', userId)
        .single();

      if (error) {
        console.error('Error fetching business info:', error);
        return;
      }

      if (data) {
        setBusinessInfo({
          business_name: data.name || '',
          business_email: data.email || email || '',
          business_phone: data.phone || '',
          business_address: data.address || ''
        });
      }
    } catch (err) {
      console.error('Error fetching business info:', err);
    }
  };

  const saveBusinessInfo = async () => {
    if (!supabase || !businessId) return;
    
    setBusinessInfoLoading(true);
    setBusinessInfoError(null);
    
    try {
      const { error } = await supabase
        .from('businesses')
        .update({
          name: businessInfo.business_name,
          email: businessInfo.business_email,
          phone: businessInfo.business_phone,
          address: businessInfo.business_address
        })
        .eq('id', businessId);

      if (error) {
        console.error('Error saving business info:', error);
        setBusinessInfoError('Failed to save business information. Please try again.');
        return;
      }

      // Update the business name state as well
      setBusinessName(businessInfo.business_name);
      
    } catch (err) {
      console.error('Error saving business info:', err);
      setBusinessInfoError('Failed to save business information. Please try again.');
    } finally {
      setBusinessInfoLoading(false);
    }
  };

  const loadTeamMembers = async (businessId: string) => {
    if (!supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .eq('business_id', businessId)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading team members:', error);
        return;
      }

      setCreatedTeamMembers(data || []);
    } catch (err) {
      console.error('Error loading team members:', err);
    }
  };

  const loadServiceAreas = async (businessId: string) => {
    if (!supabase) return;
    
    try {
      setServiceAreaLoading(true);
      const { data, error } = await supabase
        .from('service_areas')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setServiceAreas(data || []);
    } catch (err) {
      console.error('Error loading service areas:', err);
      setServiceAreaError(err instanceof Error ? err.message : 'Failed to load service areas');
    } finally {
      setServiceAreaLoading(false);
    }
  };

  const handleAddPostalCode = () => {
    setNewServiceArea(prev => ({
      ...prev,
      postal_codes: [...prev.postal_codes, '']
    }));
  };

  const handleRemovePostalCode = (index: number) => {
    setNewServiceArea(prev => ({
      ...prev,
      postal_codes: prev.postal_codes.filter((_, i) => i !== index)
    }));
  };

  const handlePostalCodeChange = (index: number, value: string) => {
    // Limit to 3 digits and auto-capitalize
    const processedValue = value.slice(0, 3).toUpperCase();
    
    setNewServiceArea(prev => ({
      ...prev,
      postal_codes: prev.postal_codes.map((code, i) => 
        i === index ? processedValue : code
      )
    }));
  };

  const handleServiceAreaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId || !supabase) return;

    try {
      setServiceAreaLoading(true);
      setServiceAreaError(null);

      // Validate inputs
      if (!newServiceArea.name.trim()) {
        throw new Error('Service area name is required');
      }

      const validPostalCodes = newServiceArea.postal_codes.filter(code => code.trim());
      if (validPostalCodes.length === 0) {
        throw new Error('At least one postal code is required');
      }

      if (editingServiceArea) {
        // Update existing service area
        const { error: updateError } = await supabase
          .from('service_areas')
          .update({
            name: newServiceArea.name.trim(),
            postal_codes: validPostalCodes
          })
          .eq('id', editingServiceArea.id);

        if (updateError) throw updateError;
      } else {
        // Create service area
        const { error: createError } = await supabase
          .from('service_areas')
          .insert({
            business_id: businessId,
            name: newServiceArea.name.trim(),
            postal_codes: validPostalCodes
          });

        if (createError) throw createError;
      }

      // Reset form, close form, and reload service areas
      setNewServiceArea({ name: '', postal_codes: [''] });
      setEditingServiceArea(null);
      setShowServiceAreaForm(false);
      await loadServiceAreas(businessId);
    } catch (err) {
      console.error('Error creating/updating service area:', err);
      setServiceAreaError(err instanceof Error ? err.message : 'Failed to create/update service area');
    } finally {
      setServiceAreaLoading(false);
    }
  };

  const handleEditServiceArea = (serviceArea: any) => {
    setEditingServiceArea(serviceArea);
    setNewServiceArea({
      name: serviceArea.name,
      postal_codes: serviceArea.postal_codes.length > 0 ? serviceArea.postal_codes : ['']
    });
  };

  const handleCancelEdit = () => {
    setEditingServiceArea(null);
    setNewServiceArea({ name: '', postal_codes: [''] });
  };

  const handleDeleteServiceArea = async (serviceArea: any) => {
    if (!businessId || !supabase) return;

    try {
      setServiceAreaLoading(true);
      setServiceAreaError(null);

      const { error: deleteError } = await supabase
        .from('service_areas')
        .delete()
        .eq('id', serviceArea.id);

      if (deleteError) throw deleteError;

      // Close confirmation and reload service areas
      setDeletingServiceArea(null);
      await loadServiceAreas(businessId);
    } catch (err) {
      console.error('Error deleting service area:', err);
      setServiceAreaError(err instanceof Error ? err.message : 'Failed to delete service area');
    } finally {
      setServiceAreaLoading(false);
    }
  };

  const updateSetupStatus = async (step: number, completed: boolean = false) => {
    if (!userId || !supabase) return;

    try {
      await supabase
        .from('user_setup_status')
        .upsert({
          user_id: userId,
          setup_step: `step_${step}`,
          current_step: step,
          completed: completed,
          setup_data: setupData
        });
    } catch (err) {
      console.error('Error updating setup status:', err);
    }
  };

  const handleNextStep = async () => {
    // Save business information if we're on the business info step
    if (currentStep === 2) {
      await saveBusinessInfo();
    }
    
    if (currentStep < setupSteps.length) {
      await updateSetupStatus(currentStep, true);
      setCurrentStep(currentStep + 1);
    } else {
      await completeSetup();
    }
  };

  const handlePreviousStep = async () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleIndustrySelect = (industry: string) => {
    setSelectedIndustry(industry);
    setSetupData((prev: any) => ({ ...prev, industry }));
  };

  const handleServiceTypeSelect = async (serviceType: string) => {
    setSelectedServiceType(serviceType);
    setSetupData((prev: any) => ({ ...prev, serviceType }));

    // Configure application settings based on service type
    if (!userId || !supabase) return;

    try {
      // Get the business ID for this user
      const { data: businessData, error: businessError } = await supabase
        .from('businesses')
        .select('id')
        .eq('owner_id', userId)
        .single();

      if (businessError || !businessData) {
        console.error('Error fetching business information');
        return;
      }

      // Configure application settings based on service type
      let homeScreen = 'location_calendar';
      let disablePages: string[] = [];

      switch (serviceType) {
        case 'Mobile':
          homeScreen = 'mobile_calendar';
          disablePages = ['location_calendar'];
          break;
        case 'Location':
          homeScreen = 'location_calendar';
          disablePages = ['mobile_calendar'];
          break;
        case 'Both':
          homeScreen = 'location_calendar';
          disablePages = [];
          break;
      }

      // Update application settings
      await supabase
        .from('application_settings')
        .upsert({
          business_id: businessData.id,
          home_screen: homeScreen,
          disable_pages: disablePages
        });

    } catch (err) {
      console.error('Error configuring application settings:', err);
    }
  };

  const handleTeamOptionSelect = (option: string) => {
    setSelectedTeamOption(option);
      setShowTeamForm(true);
    setSetupData((prev: any) => ({ ...prev, teamOption: option }));
  };

  const handleJustMeFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId || !supabase) return;

    try {
      setJustMeFormLoading(true);
      setJustMeFormError(null);

      // Basic validation
      if (!justMeFormData.first_name.trim()) {
        throw new Error('First name is required');
      }
      if (!justMeFormData.last_name.trim()) {
        throw new Error('Last name is required');
      }
      if (!justMeFormData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(justMeFormData.email.trim())) {
        throw new Error('Valid email is required');
      }

      // Initialize compensation values
      const compensationData = {
        hourly_rate: 0,
        flat_rate_amount: 0,
        commission_percentage: 0,
        commission_rate: 0
      };

      // Set compensation value based on type
      switch (justMeFormData.compensation_type) {
        case 'hourly':
          const hourlyRate = parseFloat(justMeFormData.hourly_rate || '0');
          if (isNaN(hourlyRate)) {
            throw new Error('Please enter a valid hourly rate');
          }
          compensationData.hourly_rate = hourlyRate;
          break;
        case 'flat_rate':
          const flatRate = parseFloat(justMeFormData.flat_rate_amount || '0');
          if (isNaN(flatRate)) {
            throw new Error('Please enter a valid flat rate amount');
          }
          compensationData.flat_rate_amount = flatRate;
          break;
        case 'commission':
          const commissionRate = parseFloat(justMeFormData.commission_percentage || '0');
          if (isNaN(commissionRate) || commissionRate > 100) {
            throw new Error('Please enter a valid commission rate (0-100)');
          }
          compensationData.commission_percentage = commissionRate;
          compensationData.commission_rate = commissionRate;
          break;
      }

      // Create the resource payload
      const resourcePayload = {
        business_id: businessId,
        first_name: justMeFormData.first_name.trim(),
        last_name: justMeFormData.last_name.trim(),
        email: justMeFormData.email.trim(),
        phone: justMeFormData.phone.trim(),
        role: 'Team Member',
        status: 'active',
        compensation_type: justMeFormData.compensation_type,
        is_admin: false,
        password: 'tempPassword123', // Temporary password for setup
        confirm_password: 'tempPassword123', // Temporary password for setup
        last_active: new Date().toISOString(),
        earnings_ytd: 0,
        earnings_mtd: 0,
        earnings_last_month: 0,
        is_mobile_available: false,
        schedule_availability: {
          monday: { enabled: false, shifts: [] },
          tuesday: { enabled: false, shifts: [] },
          wednesday: { enabled: false, shifts: [] },
          thursday: { enabled: false, shifts: [] },
          friday: { enabled: false, shifts: [] },
          saturday: { enabled: false, shifts: [] },
          sunday: { enabled: false, shifts: [] }
        },
        ...compensationData
      };

      // Import createResource function
      const { createResource } = await import('../../lib/resources');
      await createResource(resourcePayload);

      // Add to created team members
      setCreatedTeamMembers((prev) => [...prev, {
        id: Date.now(), // Temporary ID
        first_name: justMeFormData.first_name.trim(),
        last_name: justMeFormData.last_name.trim(),
        email: justMeFormData.email.trim(),
        phone: justMeFormData.phone.trim(),
        compensation_type: justMeFormData.compensation_type,
        hourly_rate: compensationData.hourly_rate,
        flat_rate_amount: compensationData.flat_rate_amount,
        commission_percentage: compensationData.commission_percentage
      }]);

      // Reset form
      setJustMeFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        compensation_type: 'hourly',
        hourly_rate: '0',
        flat_rate_amount: '0',
        commission_percentage: '0',
        notes: ''
      });

      setShowTeamForm(false);
    } catch (err) {
      console.error('Error creating team member:', err);
      setJustMeFormError(err instanceof Error ? err.message : 'Failed to create team member');
    } finally {
      setJustMeFormLoading(false);
    }
  };



  const handleSkipStep = async () => {
    await handleNextStep();
  };

  const handleFinalStepSelection = async (selection: string) => {
    setFinalStepSelection(selection);
    
    if (selection === 'no') {
      // Complete setup and log user in
      await completeSetup();
    }
    // If selection is 'yes', it will remain greyed out for now
  };

  const completeSetup = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🚀 Starting setup completion...', { userId, supabase: !!supabase });

      // Update setup status to completed
      if (userId && supabase) {
        console.log('📝 Updating setup status...');
        
        const { data: setupResponse, error: setupError } = await supabase
          .from('user_setup_status')
          .upsert({
            user_id: userId,
            setup_step: 'completed',
            current_step: setupSteps.length,
            completed: true,
            setup_data: setupData
          }, {
            onConflict: 'user_id'
          });

        if (setupError) {
          console.error('❌ Error updating setup status:', setupError);
          throw new Error(`Failed to update setup status: ${setupError.message}`);
        }

        console.log('✅ Setup marked as completed');
      } else {
        console.error('❌ Missing userId or supabase client');
        throw new Error('Missing required data for setup completion');
      }

      // Load business data and determine route (similar to login flow)
      if (userId && supabase) {
        try {
          console.log('🏢 Loading business data...');
          
          // Load businesses for the user
          const { data: businesses, error: businessError } = await supabase
            .from('businesses')
            .select('*')
            .eq('owner_id', userId)
            .order('created_at', { ascending: true });

          if (businessError) {
            console.error('❌ Error loading businesses:', businessError);
            throw new Error(`Failed to load business data: ${businessError.message}`);
          }

          if (!businesses || businesses.length === 0) {
            console.log('No businesses found for user');
            navigate('/calendarV2');
          return;
        }

          // Use the first business
          const selectedBusiness = businesses[0];
          
          // Check if user needs to activate subscription (including trial)
          const isBusinessActive = selectedBusiness.subscription_status === 'active' || 
            (selectedBusiness.trial_enabled && 
             selectedBusiness.trial_end_date && 
             new Date(selectedBusiness.trial_end_date) > new Date());
          
          if (!isBusinessActive) {
            console.log('📋 User subscription is inactive and no active trial, redirecting to subscription activation');
            // Store business data for quick access
            localStorage.setItem('selectedBusiness', JSON.stringify(selectedBusiness));
            localStorage.setItem('businesses', JSON.stringify(businesses));
            // Redirect to subscription management to activate the plan
            navigate('/admin-subscription-management', { replace: true });
            return;
          }
          
          // If user has already paid (active subscription or trial), proceed to main app
          if (isBusinessActive) {
            console.log('✅ User has active subscription or trial, proceeding to main app');
          }
          
          // Store business data for quick access
          localStorage.setItem('selectedBusiness', JSON.stringify(selectedBusiness));
          localStorage.setItem('businesses', JSON.stringify(businesses));

          // Determine the home screen based on application settings
          let targetRoute = '/calendarV2'; // Default route

          try {
            // Try direct RPC first for better reliability
            const { data: directData, error: directError } = await supabase.rpc(
              'get_home_screen_direct',
              { business_id_param: selectedBusiness.id }
            );

            if (!directError && directData && directData.home_screen) {
              targetRoute = directData.home_screen === 'mobile_calendar' ? '/mobile-calendar' : '/calendarV2';
            }
          } catch (error) {
            console.log('Direct query failed, trying regular query...');
            
            // Fallback to regular table query
            const { data: settings, error: settingsError } = await supabase
              .from('application_settings')
              .select('*')
              .eq('business_id', selectedBusiness.id);

            if (!settingsError && settings && settings.length > 0) {
              const setting = settings[0];
              targetRoute = setting.home_screen === 'mobile_calendar' ? '/mobile-calendar' : '/calendarV2';
            }
          }

          // Set flags for smooth navigation
          localStorage.setItem('usePreloadedBusinessData', 'true');
          localStorage.setItem('skipInitializing', 'true');
          localStorage.setItem('preloadedRoute', targetRoute);

          console.log('✅ Setup completed, navigating to:', targetRoute);
          navigate(targetRoute, { replace: true });
          return;

        } catch (error) {
          console.error('❌ Error loading business data:', error);
          // Fallback to default route
          console.log('🔄 Falling back to default route...');
          navigate('/calendarV2', { replace: true });
          return;
        }
      }

      // Fallback navigation
      navigate('/calendarV2', { replace: true });
    } catch (err) {
      console.error('❌ Error completing setup:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to complete setup: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            {/* Nexus AI Assistant - Organic Design */}
            <div className="text-center space-y-6">
              {/* Nexus AI Avatar */}
              <div className="flex justify-center">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center shadow-2xl">
                  <Bot className="h-10 w-10 text-white" />
                </div>
              </div>

              {/* Nexus AI Title */}
              <div className="flex items-center justify-center space-x-3">
                <h3 className="text-2xl font-bold text-white">Nexus AI</h3>
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>

              {/* Welcome Message */}
              <div className="max-w-2xl mx-auto">
                <p className="text-white/90 text-lg leading-relaxed">
                  Hello, I am Nexus! I'm excited to help you set up{' '}
                  <span className="font-bold text-purple-300">
                    {businessName || 'your business'}
                  </span>
                  . Let's get started with a few simple steps to tailor your system.
                </p>
              </div>
            </div>
          </div>
        );

      case 2:
        // Business Information step
        return (
          <div className="space-y-6">
            {/* Nexus AI Assistant - Business Information */}
            <div className="text-center space-y-6">
              {/* Nexus AI Avatar */}
              <div className="flex justify-center">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center shadow-2xl">
                  <Bot className="h-10 w-10 text-white" />
                </div>
              </div>

              {/* Nexus AI Title */}
              <div className="flex items-center justify-center space-x-3">
                <h3 className="text-2xl font-bold text-white">Nexus AI</h3>
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>

              {/* Business Information Message */}
              <div className="max-w-2xl mx-auto">
                <p className="text-white/90 text-lg leading-relaxed mb-8">
                  Now let's add your business info. This info is visible throughout different areas of the system like invoices, public forms and other areas.
                </p>
              </div>

              {/* Business Information Form */}
              <div className="max-w-2xl mx-auto">
                <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
                  {businessInfoError && (
                    <div className="mb-6 bg-red-500/20 border border-red-400/30 text-red-200 px-4 py-3 rounded-xl text-sm">
                      {businessInfoError}
                    </div>
                  )}
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-white/90 mb-2 flex items-center">
                        <Building2 className="h-4 w-4 mr-2" />
                        Business Name
                      </label>
                      <input
                        type="text"
                        value={businessInfo.business_name || businessName || ''}
                        onChange={(e) => setBusinessInfo(prev => ({ ...prev, business_name: e.target.value }))}
                        className="w-full h-12 rounded-xl border-white/20 bg-white/10 text-white placeholder-white/50 focus:border-purple-400 focus:ring-purple-400 text-sm backdrop-blur-lg"
                        placeholder="Enter business name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-white/90 mb-2 flex items-center">
                        <Mail className="h-4 w-4 mr-2" />
                        Business Email
                      </label>
                      <input
                        type="email"
                        value={businessInfo.business_email || email || ''}
                        onChange={(e) => setBusinessInfo(prev => ({ ...prev, business_email: e.target.value }))}
                        className="w-full h-12 rounded-xl border-white/20 bg-white/10 text-white placeholder-white/50 focus:border-purple-400 focus:ring-purple-400 text-sm backdrop-blur-lg"
                        placeholder="Enter business email"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-white/90 mb-2 flex items-center">
                        <Phone className="h-4 w-4 mr-2" />
                        Business Phone Number
                      </label>
                      <input
                        type="tel"
                        value={businessInfo.business_phone}
                        onChange={(e) => setBusinessInfo(prev => ({ ...prev, business_phone: e.target.value }))}
                        className="w-full h-12 rounded-xl border-white/20 bg-white/10 text-white placeholder-white/50 focus:border-purple-400 focus:ring-purple-400 text-sm backdrop-blur-lg"
                        placeholder="Enter business phone number"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-white/90 mb-2 flex items-center">
                        <MapPin className="h-4 w-4 mr-2" />
                        Business Address
                      </label>
                      <input
                        type="text"
                        value={businessInfo.business_address}
                        onChange={(e) => setBusinessInfo(prev => ({ ...prev, business_address: e.target.value }))}
                        className="w-full h-12 rounded-xl border-white/20 bg-white/10 text-white placeholder-white/50 focus:border-purple-400 focus:ring-purple-400 text-sm backdrop-blur-lg"
                        placeholder="Enter business address"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            {/* Nexus AI Assistant - Industry Question */}
            <div className="text-center space-y-6">
              {/* Nexus AI Avatar */}
              <div className="flex justify-center">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center shadow-2xl">
                  <Bot className="h-10 w-10 text-white" />
                </div>
              </div>

              {/* Nexus AI Title */}
              <div className="flex items-center justify-center space-x-3">
                <h3 className="text-2xl font-bold text-white">Nexus AI</h3>
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>

              {/* Industry Question */}
              <div className="max-w-2xl mx-auto">
                <p className="text-white/90 text-lg leading-relaxed mb-8">
                  What industry are you in?
                </p>
              </div>

              {/* Industry Options */}
              <div className="max-w-md mx-auto space-y-4">
                <button
                  onClick={() => handleIndustrySelect('Automotive')}
                  className={`w-full p-6 rounded-xl border-2 transition-all duration-300 ease-out transform ${
                    selectedIndustry === 'Automotive'
                      ? 'border-purple-400 bg-purple-500/30 text-white scale-105 shadow-lg shadow-purple-500/25'
                      : 'border-white/30 bg-white/5 text-white/80 hover:border-purple-400/50 hover:bg-white/10 hover:scale-105 active:scale-95'
                  }`}
                >
                  <div className="text-center">
                    <h4 className="text-xl font-bold mb-2">Automotive</h4>
                    <p className="text-sm opacity-80">Car repair, maintenance, and services</p>
                  </div>
                  {selectedIndustry === 'Automotive' && (
                    <div className="absolute top-3 right-3">
                      <div className="w-6 h-6 bg-purple-400 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}
                </button>

                <button
                  disabled
                  className="w-full p-6 rounded-xl border-2 transition-all duration-300 ease-out transform relative border-gray-500/30 bg-gray-500/10 text-gray-400 cursor-not-allowed opacity-60"
                >
                  <div className="text-center">
                    <h4 className="text-xl font-bold mb-2">Home Services</h4>
                    <p className="text-sm opacity-80">Cleaning, maintenance, and home care</p>
                    <div className="mt-2">
                      <span className="text-xs bg-gray-600/50 text-gray-300 px-2 py-1 rounded-full">
                        Coming Soon
                      </span>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            {/* Nexus AI Assistant - Service Type Question */}
            <div className="text-center space-y-6">
              {/* Nexus AI Avatar */}
              <div className="flex justify-center">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center shadow-2xl">
                  <Bot className="h-10 w-10 text-white" />
                </div>
              </div>

              {/* Nexus AI Title */}
              <div className="flex items-center justify-center space-x-3">
                <h3 className="text-2xl font-bold text-white">Nexus AI</h3>
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>

              {/* Service Type Question */}
              <div className="max-w-2xl mx-auto">
                <p className="text-white/90 text-lg leading-relaxed mb-8">
                  Is your business a Mobile Service or location based service?
                </p>
              </div>

              {/* Service Type Options */}
              <div className="max-w-md mx-auto space-y-4">
                <button
                  onClick={() => handleServiceTypeSelect('Mobile')}
                  className={`w-full p-6 rounded-xl border-2 transition-all duration-300 ease-out transform relative ${
                    selectedServiceType === 'Mobile'
                      ? 'border-purple-400 bg-purple-500/30 text-white scale-105 shadow-lg shadow-purple-500/25'
                      : 'border-white/30 bg-white/5 text-white/80 hover:border-purple-400/50 hover:bg-white/10 hover:scale-105 active:scale-95'
                  }`}
                >
                  <div className="text-center">
                    <h4 className="text-xl font-bold mb-2">Mobile</h4>
                    <p className="text-sm opacity-80">Travel to customer locations</p>
                  </div>
                  {selectedServiceType === 'Mobile' && (
                    <div className="absolute top-3 right-3">
                      <div className="w-6 h-6 bg-purple-400 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}
                </button>

                <button
                  onClick={() => handleServiceTypeSelect('Location')}
                  className={`w-full p-6 rounded-xl border-2 transition-all duration-300 ease-out transform relative ${
                    selectedServiceType === 'Location'
                      ? 'border-purple-400 bg-purple-500/30 text-white scale-105 shadow-lg shadow-purple-500/25'
                      : 'border-white/30 bg-white/5 text-white/80 hover:border-purple-400/50 hover:bg-white/10 hover:scale-105 active:scale-95'
                  }`}
                >
                  <div className="text-center">
                    <h4 className="text-xl font-bold mb-2">Location</h4>
                    <p className="text-sm opacity-80">Fixed location or storefront</p>
                  </div>
                  {selectedServiceType === 'Location' && (
                    <div className="absolute top-3 right-3">
                      <div className="w-6 h-6 bg-purple-400 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}
                </button>

                <button
                  onClick={() => handleServiceTypeSelect('Both')}
                  className={`w-full p-6 rounded-xl border-2 transition-all duration-300 ease-out transform relative ${
                    selectedServiceType === 'Both'
                      ? 'border-purple-400 bg-purple-500/30 text-white scale-105 shadow-lg shadow-purple-500/25'
                      : 'border-white/30 bg-white/5 text-white/80 hover:border-purple-400/50 hover:bg-white/10 hover:scale-105 active:scale-95'
                  }`}
                >
                  <div className="text-center">
                    <h4 className="text-xl font-bold mb-2">Both</h4>
                    <p className="text-sm opacity-80">Mobile and location-based services</p>
                  </div>
                  {selectedServiceType === 'Both' && (
                    <div className="absolute top-3 right-3">
                      <div className="w-6 h-6 bg-purple-400 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
        );

      case 5:
        // Completed Basic Setup step (all flows)
          return (
            <div className="space-y-6">
            {/* Nexus AI Assistant - Final Step */}
              <div className="text-center space-y-6">
                {/* Nexus AI Avatar */}
                <div className="flex justify-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center shadow-2xl">
                    <Bot className="h-10 w-10 text-white" />
                  </div>
                </div>

                {/* Nexus AI Title */}
                <div className="flex items-center justify-center space-x-3">
                  <h3 className="text-2xl font-bold text-white">Nexus AI</h3>
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </div>

                            {/* Completion Message */}
                <div className="max-w-2xl mx-auto">
                  <p className="text-white/90 text-lg leading-relaxed mb-8">
                  Your basic setup is completed. Welcome to Nexus!
                  </p>
                </div>
              </div>
            </div>
          );

      default:
        return null;
    }
  };

  const loadPermissionRoles = async () => {
    if (!businessId) return;
    
    try {
      const roles = await getResourceRoles(businessId);
      setPermissionRoles(roles);
    } catch (err) {
      console.error('Error loading permission roles:', err);
      setTeamMemberError(err instanceof Error ? err.message : 'Failed to load permission roles');
    }
  };

  const handleTeamMemberFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🔍 [DEBUG] Starting team member creation process');
    console.log('🔍 [DEBUG] Business ID:', businessId);
    console.log('🔍 [DEBUG] Form data:', teamMemberFormData);
    
    if (!businessId) {
      console.error('❌ [DEBUG] No business selected');
      setTeamMemberError('No business selected');
      return;
    }

    try {
      setTeamMemberLoading(true);
      setTeamMemberError(null);
      console.log('🔍 [DEBUG] Starting validation...');

      // Basic validation
      if (!teamMemberFormData.first_name.trim()) {
        console.error('❌ [DEBUG] First name validation failed');
        throw new Error('First name is required');
      }
      if (!teamMemberFormData.last_name.trim()) {
        console.error('❌ [DEBUG] Last name validation failed');
        throw new Error('Last name is required');
      }
      if (!teamMemberFormData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(teamMemberFormData.email.trim())) {
        console.error('❌ [DEBUG] Email validation failed:', teamMemberFormData.email);
        throw new Error('Valid email is required');
      }
      if (!teamMemberFormData.role.trim()) {
        console.error('❌ [DEBUG] Role validation failed');
        throw new Error('Job role is required');
      }
      if (!teamMemberFormData.password) {
        console.error('❌ [DEBUG] Password validation failed');
        throw new Error('Password is required');
      }

      // Validate password
      if (teamMemberFormData.password.length < 6) {
        console.error('❌ [DEBUG] Password length validation failed');
        throw new Error('Password must be at least 6 characters');
      }
      if (teamMemberFormData.password !== teamMemberFormData.confirm_password) {
        console.error('❌ [DEBUG] Password confirmation validation failed');
        throw new Error('Passwords do not match');
      }

      console.log('✅ [DEBUG] All validations passed');

      // Initialize compensation values
      const compensationData = {
        hourly_rate: 0,
        flat_rate_amount: 0,
        commission_percentage: 0,
        commission_rate: 0
      };

      // Set compensation based on type
      if (teamMemberFormData.compensation_type === 'hourly') {
        compensationData.hourly_rate = parseFloat(teamMemberFormData.hourly_rate) || 0;
        console.log('🔍 [DEBUG] Setting hourly rate:', compensationData.hourly_rate);
      } else if (teamMemberFormData.compensation_type === 'flat_rate') {
        compensationData.flat_rate_amount = parseFloat(teamMemberFormData.flat_rate_amount) || 0;
        console.log('🔍 [DEBUG] Setting flat rate amount:', compensationData.flat_rate_amount);
      } else if (teamMemberFormData.compensation_type === 'commission') {
        compensationData.commission_percentage = parseFloat(teamMemberFormData.commission_percentage) || 0;
        compensationData.commission_rate = compensationData.commission_percentage / 100;
        console.log('🔍 [DEBUG] Setting commission percentage:', compensationData.commission_percentage);
      }

      console.log('🔍 [DEBUG] Compensation data:', compensationData);

      // Create the resource payload
      const resourcePayload = {
        business_id: businessId,
        first_name: teamMemberFormData.first_name.trim(),
        last_name: teamMemberFormData.last_name.trim(),
        email: teamMemberFormData.email.trim(),
        phone: teamMemberFormData.phone.trim(),
        role: teamMemberFormData.role.trim(),
        role_id: teamMemberFormData.role_id || undefined,
        status: teamMemberFormData.status,
        compensation_type: teamMemberFormData.compensation_type,
        is_admin: teamMemberFormData.is_admin,
        password: teamMemberFormData.password,
        confirm_password: teamMemberFormData.confirm_password,
        earnings_ytd: 0,
        earnings_mtd: 0,
        earnings_last_month: 0,
        is_mobile_available: false,
        schedule_availability: {
          monday: { enabled: false, shifts: [] },
          tuesday: { enabled: false, shifts: [] },
          wednesday: { enabled: false, shifts: [] },
          thursday: { enabled: false, shifts: [] },
          friday: { enabled: false, shifts: [] },
          saturday: { enabled: false, shifts: [] },
          sunday: { enabled: false, shifts: [] }
        },
        ...compensationData
      };

      console.log('🔍 [DEBUG] Resource payload created:', resourcePayload);
      console.log('🔍 [DEBUG] About to call createResource...');

      const result = await createResource(resourcePayload);
      console.log('✅ [DEBUG] createResource completed successfully:', result);
      
      // Reset form and hide it
      setTeamMemberFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: '',
        role_id: '',
        compensation_type: 'hourly',
        hourly_rate: '0',
        flat_rate_amount: '0',
        commission_percentage: '0',
        status: 'active',
        is_admin: false,
        password: '',
        confirm_password: '',
        notes: ''
      });
      setShowTeamMemberForm(false);
      
      // Reload team members to show the newly created one
      if (businessId) {
        loadTeamMembers(businessId);
      }
      
      console.log('✅ [DEBUG] Team member created successfully');
    } catch (err) {
      console.error('❌ [DEBUG] Error creating resource:', err);
      console.error('❌ [DEBUG] Error details:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined,
        name: err instanceof Error ? err.name : undefined
      });
      setTeamMemberError(err instanceof Error ? err.message : 'Failed to create team member');
    } finally {
      console.log('🔍 [DEBUG] Setting loading to false');
      setTeamMemberLoading(false);
    }
  };

  const handleTeamMemberCreated = () => {
    setShowTeamMemberForm(false);
    // Reload team members to show the newly created one
    if (businessId) {
      loadTeamMembers(businessId);
    }
  };

  // Handler functions for Apply Area to Team step
  const handleMobileToggle = async (memberId: string) => {
    setMobileLoading(memberId);
    try {
      // TODO: Implement mobile toggle functionality
      console.log('Toggling mobile availability for member:', memberId);
      // This would update the member's is_mobile_available status
    } catch (error) {
      console.error('Error toggling mobile availability:', error);
    } finally {
      setMobileLoading(null);
    }
  };

  const handleWorkingHoursToggle = (memberId: string, day: string, enabled: boolean) => {
    // TODO: Implement working hours toggle functionality
    console.log('Toggling working hours for member:', memberId, 'day:', day, 'enabled:', enabled);
  };

  const handleWorkingHoursChange = (memberId: string, day: string, type: 'start' | 'end', value: string) => {
    // TODO: Implement working hours change functionality
    console.log('Changing working hours for member:', memberId, 'day:', day, 'type:', type, 'value:', value);
  };

  const handleServiceAreaToggle = (memberId: string, areaId: string, enabled: boolean) => {
    // TODO: Implement service area toggle functionality
    console.log('Toggling service area for member:', memberId, 'area:', areaId, 'enabled:', enabled);
  };

  if (!userId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-purple-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-white/30 border-t-white rounded-full animate-spin mx-auto shadow-lg"></div>
          <p className="mt-6 text-white/80 font-medium">Loading setup...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-purple-900 flex items-center justify-center relative overflow-hidden px-4" style={{
      paddingTop: 'env(safe-area-inset-top, 0px)',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }}>
      {/* Subtle Tech Pattern Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>

      {/* Main Container */}
      <div className="w-full max-w-4xl relative z-10 transform transition-all duration-700 ease-out animate-in slide-in-from-bottom-4 fade-in-0">
        {/* Logo Section */}
        <div className="text-center mb-8 relative">
          <h1 className="text-5xl font-black text-white mb-4 tracking-wider" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            Nexus <span className="text-4xl align-super">AI</span>
          </h1>
          <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-white/60 to-transparent mx-auto"></div>
          <p className="mt-4 text-white/80 text-lg font-medium">Complete Your Setup</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-4">
            {setupSteps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className={`flex items-center ${currentStep >= step.id ? 'text-purple-400' : 'text-white/40'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                    currentStep >= step.id ? 'border-purple-400 bg-purple-400/20' : 'border-white/40'
                  }`}>
                    {currentStep > step.id ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <span className="text-sm font-semibold">{step.id}</span>
                    )}
                  </div>
                  <div className="ml-3 text-left">
                    <span className="text-sm font-medium block">{step.title}</span>
                    <span className="text-xs text-white/60 hidden md:block">{step.description}</span>
                  </div>
                </div>
                {index < setupSteps.length - 1 && (
                  <div className={`w-8 h-0.5 ${currentStep > step.id ? 'bg-purple-400' : 'bg-white/20'}`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Setup Content Card */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-2xl shadow-purple-900/20 transition-all duration-300 ease-out hover:shadow-purple-900/30" style={{
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)'
        }}>
          {/* Error Message */}
          {error && (
            <div className="bg-red-500/20 border border-red-400/30 rounded-xl p-4 backdrop-blur-sm mb-6">
              <p className="text-red-300 text-sm text-center">{error}</p>
            </div>
          )}

          {/* Step Content */}
          {renderStepContent()}

          {/* Action Buttons */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-white/10">
            <div className="text-white/60 text-sm">
              Step {currentStep} of {setupSteps.length}
            </div>
            
            <div className="flex justify-between items-center space-x-4">
              {/* Back Button - Show on all steps except the first */}
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handlePreviousStep}
                  className="bg-transparent border border-white/30 hover:border-white/50 text-white/80 hover:text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 touch-manipulation flex items-center space-x-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back</span>
                </button>
              )}
              
              {/* Skip Step Button - Show on specific steps */}
              {(currentStep !== 2 && currentStep !== 3 && !(currentStep === 4 && selectedServiceType === 'Mobile' && !showTeamForm) && !(currentStep === 4 && selectedServiceType === 'Mobile' && showTeamForm) && !(currentStep === 4 && selectedServiceType !== 'Mobile') && !(currentStep === 5 && selectedServiceType === 'Mobile')) && (
                <button
                  type="button"
                  onClick={handleSkipStep}
                  className="bg-transparent border border-purple-400/30 hover:border-purple-400 text-purple-400 hover:text-purple-300 font-semibold py-3 px-6 rounded-xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 touch-manipulation"
                >
                  {currentStep === setupSteps.length ? 'Complete Setup' : 'Skip Step'}
                </button>
              )}
              
                             {/* Next Step Button - Hide on final step */}
               {currentStep !== 8 && (
                <button
                  type="button"
                  onClick={handleNextStep}
                                    disabled={loading || (currentStep === 2 && (!businessInfo.business_name || !businessInfo.business_email)) || (currentStep === 3 && !selectedIndustry) || (currentStep === 4 && !selectedServiceType)}
                  className={`font-semibold py-3 px-6 rounded-xl transition-all duration-300 ease-out transform touch-manipulation flex items-center space-x-2 ${
                      loading || (currentStep === 2 && (!businessInfo.business_name || !businessInfo.business_email)) || (currentStep === 3 && !selectedIndustry) || (currentStep === 4 && !selectedServiceType)
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed shadow-none hover:scale-100 active:scale-100'
                      : 'bg-purple-700 hover:bg-purple-600 text-white hover:scale-105 active:scale-95 shadow-lg shadow-purple-700/30 hover:shadow-purple-600/40'
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <span>{currentStep === setupSteps.length ? 'Complete Setup' : 'Next Step'}</span>
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
    </div>
  );
} 